import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReply } from '@fortawesome/free-solid-svg-icons';
import { getAutoReply, saveAutoReply } from '../../../lib/api';
import '../../admin/pages/admin.css';

const MAX_MESSAGE_LENGTH = 1000;

const AutoReply = () => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getAutoReply();
        if (!active) return;
        setIsEnabled(!!res?.data?.is_enabled);
        setMessage(res?.data?.message || '');
      } catch (err) {
        if (active) setError(err?.message || 'Failed to load auto reply settings.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setFeedback('');
    setError('');

    const trimmed = message.trim();
    if (isEnabled && !trimmed) {
      setError('Please enter the message to send automatically.');
      return;
    }
    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      setError(`Message must be ${MAX_MESSAGE_LENGTH} characters or fewer.`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await saveAutoReply({ isEnabled, message: trimmed });
      setMessage(res?.data?.message ?? trimmed);
      setIsEnabled(!!res?.data?.is_enabled);
      setFeedback(res?.message || 'Auto reply settings saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save auto reply settings.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-page">
      <header className="admin-page__hero">
        <div className="admin-page__title-row">
          <div className="admin-page__icon" aria-hidden>
            <FontAwesomeIcon icon={faReply} />
          </div>
          <div>
            <span className="admin-page__eyebrow">Account</span>
            <h1 className="admin-page__title">Auto reply</h1>
            <p className="admin-page__subtitle">
              Automatically answer every incoming WhatsApp message with a default message.
            </p>
          </div>
        </div>
      </header>

      <section className="admin-panel">
        <div className="admin-panel__head">
          <h2>Default reply</h2>
          <p>
            Each auto reply is sent as a normal chat message and Rs 0.15 is deducted from your
            wallet, the same as replying manually from Live Chat.
          </p>
        </div>
        <div className="admin-panel__body">
          <form onSubmit={onSubmit}>
            <div className="admin-field">
              <label className="admin-label" htmlFor="auto-reply-enabled">
                <input
                  id="auto-reply-enabled"
                  type="checkbox"
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                  disabled={loading || submitting}
                  style={{ marginRight: 8 }}
                />
                Enable auto reply
              </label>
            </div>

            <div className="admin-field" style={{ marginTop: 16 }}>
              <label className="admin-label" htmlFor="auto-reply-message">
                Message
              </label>
              <textarea
                id="auto-reply-message"
                className="admin-input"
                rows={5}
                maxLength={MAX_MESSAGE_LENGTH}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Thanks for reaching out! We have received your message and will get back to you shortly."
                disabled={loading || submitting}
              />
              <small style={{ color: '#6b7280' }}>
                {message.length}/{MAX_MESSAGE_LENGTH} characters
              </small>
            </div>

            {error && (
              <div className="admin-feedback admin-feedback--error" style={{ marginTop: 16 }} role="alert">
                {error}
              </div>
            )}
            {feedback && (
              <div className="admin-feedback admin-feedback--success" style={{ marginTop: 16 }} role="status">
                {feedback}
              </div>
            )}

            <div className="admin-actions" style={{ marginTop: 20 }}>
              <button
                type="submit"
                className="admin-btn admin-btn--primary"
                disabled={loading || submitting}
              >
                {submitting ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default AutoReply;
